import { useEffect, useState, type ReactNode } from "react";
import { Camera, DownloadIcon, ExternalLinkIcon, FileTextIcon, PlayIcon } from "lucide-react";

import type { ArtifactSurface } from "~/rightPanelStore";
import { cn } from "~/lib/utils";
import { Tooltip, TooltipPopup, TooltipTrigger } from "~/components/ui/tooltip";

/**
 * Full-size viewer for a generated artifact (image / pdf / video) opened in the
 * right panel. Rendered as the panel content inside the shared PreviewPanelShell
 * (which owns the resize + border chrome), so this component is just a header
 * over a scrollable body.
 */
export function ArtifactViewerPanel({ surface }: { surface: ArtifactSurface }) {
  const { artifactUrl, artifactKind, title } = surface;
  const label = title?.trim() || defaultTitle(artifactKind);
  const KindIcon =
    artifactKind === "pdf" ? FileTextIcon : artifactKind === "video" ? PlayIcon : Camera;
  // A media element asked to play with no usable source rejects play() with an
  // unhandled `NotSupportedError` (Sentry JAVASCRIPT-4) and otherwise just sits
  // there dead. So a blank URL never reaches the element, and an element that
  // fails to load says so, with the download/open links in the header still
  // there to fall back on.
  const hasUrl = artifactUrl.trim().length > 0;
  const [loadFailed, setLoadFailed] = useState(false);
  useEffect(() => {
    setLoadFailed(false);
  }, [artifactUrl]);
  const unplayable = !hasUrl || loadFailed;

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col bg-background">
      <div className="flex h-9 min-h-9 shrink-0 items-center gap-2 border-b border-border px-3">
        <KindIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
        <span className="min-w-0 flex-1 truncate text-xs font-medium text-foreground" title={label}>
          {label}
        </span>
        <div className="flex shrink-0 items-center gap-0.5">
          <ArtifactHeaderLink href={artifactUrl} label="Open in new tab">
            <ExternalLinkIcon className="size-3.5" aria-hidden />
          </ArtifactHeaderLink>
          <ArtifactHeaderLink href={artifactUrl} label="Download" download={label}>
            <DownloadIcon className="size-3.5" aria-hidden />
          </ArtifactHeaderLink>
        </div>
      </div>
      <div
        className={cn(
          "min-h-0 min-w-0 flex-1",
          // A checkerboard-free neutral ground that stays legible for both
          // transparent PNGs (light) and light-on-dark renders (dark).
          artifactKind === "image" ? "overflow-auto bg-muted/40 p-3" : "flex flex-col",
        )}
      >
        {unplayable ? (
          <div className="flex min-h-0 flex-1 items-center justify-center p-6 text-center text-xs text-muted-foreground">
            This {defaultTitle(artifactKind).toLowerCase()} could not be loaded. Try opening or
            downloading it instead.
          </div>
        ) : artifactKind === "image" ? (
          <img
            src={artifactUrl}
            alt={label}
            onError={() => setLoadFailed(true)}
            className="mx-auto h-auto max-h-full w-auto max-w-full rounded-md object-contain"
          />
        ) : artifactKind === "pdf" ? (
          <iframe
            src={artifactUrl}
            title={label}
            className="min-h-0 w-full flex-1 border-0 bg-white"
          />
        ) : (
          <video
            src={artifactUrl}
            controls
            // The element's own error event IS the handled failure path: it fires
            // for a 404 / expired / revoked source, where play() would otherwise
            // reject with an unhandled NotSupportedError.
            onError={() => setLoadFailed(true)}
            className="min-h-0 w-full flex-1 bg-black object-contain"
          />
        )}
      </div>
    </div>
  );
}

function ArtifactHeaderLink(props: {
  href: string;
  label: string;
  download?: string;
  children: ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <a
            href={props.href}
            target="_blank"
            rel="noopener noreferrer"
            {...(props.download !== undefined ? { download: props.download } : {})}
            aria-label={props.label}
            className="flex size-6 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          >
            {props.children}
          </a>
        }
      />
      <TooltipPopup>{props.label}</TooltipPopup>
    </Tooltip>
  );
}

function defaultTitle(kind: ArtifactSurface["artifactKind"]): string {
  switch (kind) {
    case "pdf":
      return "Document";
    case "video":
      return "Video";
    case "image":
      return "Image";
  }
}
